import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from '../../../hooks';

export function SEmptyState(props: SEmptyStateProps): React.JSX.Element {
    const { t } = useTranslation('polaris', { useSuspense: false });
    // const t = (key: string, options?: Record<string, string>) => 'No results found';
    const { value: primaryLoading, setTrue: setPrimaryLoading } = useToggle(!!props?.action?.loading);
    const { value: secondaryLoading, setTrue: setSecondaryLoading } = useToggle(!!props?.secondaryAction?.loading);

    const defaultTitle = useMemo(
        () =>
            t('Polaris.IndexTable.emptySearchTitle', {
                resourceNamePlural: t('Polaris.IndexProvider.defaultItemPlural'),
            }),
        [t]
    );

    return (
        <div className="flex flex-col gap-4 items-center justify-center py-8">
            <div className="max-w-50 max-h-50">
                <img src={props.image} className="aspect-square" />
            </div>

            <div className="flex flex-col gap-4 justify-center items-center max-w-md">
                <div className="flex flex-col items-center">
                    <h2 className="text-base font-semibold! text-center text-balance">
                        {props.heading ?? defaultTitle}
                    </h2>
                    {!!props.description && (
                        <span className="text-sm text-center text-balance">{props.description}</span>
                    )}
                </div>

                {(!!props.action || !!props.secondaryAction) && (
                    <s-button-group>
                        {props.secondaryAction && (
                            <s-button
                                disabled={props.secondaryAction.disabled}
                                slot="secondary-actions"
                                aria-label={props.secondaryAction.content}
                                onClick={() => {
                                    if (props.secondaryAction.url) {
                                        setSecondaryLoading();
                                        open(props.secondaryAction.url, '_self');
                                    } else {
                                        props.secondaryAction.onAction();
                                    }
                                }}
                                loading={secondaryLoading}
                                icon={props.secondaryAction.icon}
                            >
                                {props.secondaryAction.content}
                            </s-button>
                        )}
                        {props.action && (
                            <s-button
                                disabled={props.action.disabled}
                                slot="primary-action"
                                aria-label={props.action.content}
                                onClick={() => {
                                    if (props.action.url) {
                                        setPrimaryLoading();
                                        open(props.action.url, '_self');
                                    } else {
                                        props.action.onAction();
                                    }
                                }}
                                loading={primaryLoading}
                                icon={props.action.icon}
                                variant="primary"
                            >
                                {props.action.content}
                            </s-button>
                        )}
                    </s-button-group>
                )}

                {props.children}
            </div>
        </div>
    );
}

export interface SEmptyStateProps {
    image: string;
    heading?: string;
    description?: string;
    action?: SEmptyStateAction;
    secondaryAction?: SEmptyStateAction;
    children?: React.ReactNode;
}

type SEmptyStateBaseAtion = {
    content: string;
    disabled?: boolean;
    loading?: boolean;
    icon?: HTMLElementTagNameMap['s-icon']['type'];
};

type SEmptyStateActionWithUrl = SEmptyStateBaseAtion & {
    url: string;
    onAction?: never;
};

type SEmptyStateActionWithOnAction = SEmptyStateBaseAtion & {
    onAction: () => void;
    url?: never;
};

export type SEmptyStateAction = SEmptyStateActionWithUrl | SEmptyStateActionWithOnAction;
